import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNow, useRigg } from '@/data/RiggProvider.tsx';
import type { DataState } from '@/data/store.ts';
import { ORIGIN_LABELS, TASK_ORIGINS, type Task } from '@shared/domain.ts';
import {
  defaultFilters,
  defaultSort,
  SORT_OPTIONS,
  visibleTasks,
  type Filters,
  type Queue,
  type SortOrder,
} from '@/data/selectors.ts';
import { api } from '@/lib/api.ts';
import { navigate } from '@/lib/router.ts';
import { TaskRow, type RowContext } from '@/components/TaskRow.tsx';
import { ApprovalRow } from '@/components/ApprovalRow.tsx';
import { TaskDetail } from '@/components/TaskDetail.tsx';
import { Composer } from '@/components/Composer.tsx';
import { BoardLayout, LIST_CARD, ListSearch, useListKeys } from '@/components/Board.tsx';
import { OrphanPrs } from '@/components/OrphanPrs.tsx';
import { kindOptions, originText } from '@/components/TaskFacts.tsx';
import { Empty, Select, Shimmer, useToast, type Option } from '@/components/ui.tsx';

const HEADINGS: Record<Queue, { title: string; blurb: string; empty: [string, string] }> = {
  tasks: {
    title: 'Tasks',
    blurb: 'Everything in flight for this project.',
    empty: ['No tasks yet', 'Describe one below and a routine will pick it up.'],
  },
  'needs-you': {
    title: 'Needs you',
    blurb: 'Waiting on a person: a look, a decision, or an approval.',
    empty: ['You are all caught up', 'Nothing is waiting on you right now.'],
  },
  approval: {
    title: 'Awaiting approval',
    blurb: 'Open pull requests nobody has cleared to land — least risky first.',
    empty: ['Nothing to approve', 'A ready pull request waiting on a yes shows up here.'],
  },
  failed: {
    title: 'Failed',
    blurb: 'The routine could not finish, and said why.',
    empty: ['Nothing is failing', 'When a routine gets stuck, the task shows up here.'],
  },
  merging: {
    title: 'Merging',
    blurb: 'Approved and on its way in. Anything stuck here is in Needs you too.',
    empty: ['Nothing waiting to merge', 'Approved work waits here until it lands.'],
  },
  merged: {
    title: 'Merged',
    blurb: 'Landed on the base branch, not yet live.',
    empty: ['Nothing merged yet', 'Work that lands shows up here.'],
  },
  deployed: {
    title: 'Deployed',
    blurb: 'Live.',
    empty: ['Nothing deployed yet', 'Work that goes live shows up here.'],
  },
  snoozed: {
    title: 'Snoozed',
    blurb: 'Parked for later — out of the way until you wake them.',
    empty: ['Nothing snoozed', 'Snooze a task to park it here for the time being.'],
  },
  trash: {
    title: 'Trash',
    blurb: 'Deleted tasks. Restore one to bring it back.',
    empty: ['Trash is empty', 'A deleted task shows up here until it is restored.'],
  },
};

const SORT_KEY = 'rigg:sort';

function readStoredSort(): SortOrder {
  const stored = localStorage.getItem(SORT_KEY);
  return stored === 'created_desc' || stored === 'created_asc' || stored === 'updated_desc'
    ? stored
    : defaultSort;
}

export function TasksScreen({
  queue,
  selectedId,
}: {
  queue: Queue;
  selectedId: string | null;
}) {
  const { data, projects, project, allProjects, isAdminOfProject, isAdminOfTask } = useRigg();
  const now = useNow();
  const { notify } = useToast();
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sort, setSort] = useState<SortOrder>(readStoredSort);
  // Drafts live here, not in the row, so a routine reporting in mid-sentence
  // cannot wipe what you were typing.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Read by the row callbacks below without making them (and therefore every
  // row's `ctx`) change identity on every keystroke — see the comment on
  // `ConnectedTaskRow`.
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  useEffect(() => {
    localStorage.setItem(SORT_KEY, sort);
  }, [sort]);

  const rows = useMemo(
    () => visibleTasks(data, queue, filters, isAdminOfTask, now, sort),
    [data, queue, filters, isAdminOfTask, now, sort],
  );

  const selected = selectedId ? (data.tasks[selectedId] ?? null) : null;
  const searching = filters.query.trim() !== '';
  const heading = HEADINGS[queue];
  const blurb =
    allProjects && queue === 'tasks' ? 'Everything in flight, across every project.' : heading.blurb;

  const select = useCallback(
    (id: string | null) => navigate(id ? `/t/${id}` : '/', true),
    [],
  );

  const searchRef = useRef<HTMLInputElement>(null);

  const rowIds = useMemo(() => rows.map((task) => task.id), [rows]);
  useListKeys(rowIds, selectedId, select, searchRef);

  const act = useCallback(
    async (label: string, fn: () => Promise<unknown>) => {
      try {
        await fn();
      } catch (error) {
        notify(label, { body: (error as Error).message, tone: 'bad' });
      }
    },
    [notify],
  );

  /*
   * Stable per-list handlers, not one closure per row.
   *
   * `ConnectedTaskRow` builds each row's `ctx` in its own `useMemo`, which
   * only earns its keep if what it depends on actually stays the same
   * reference when nothing about that row changed. A handler created fresh
   * per row on every render of this list — closing over that row's
   * `task.id` — defeats that immediately: even a keystroke in one row's
   * draft box would hand every *other* row a "new" `onComment` and force it
   * to re-render too. These take the id as an argument instead, so they can
   * be defined once, and each row binds its own id in the one small
   * `useCallback` that lives inside `ConnectedTaskRow`.
   */
  const onDraftChange = useCallback((taskId: string, value: string) => {
    setDrafts((d) => ({ ...d, [taskId]: value }));
  }, []);

  const onRowComment = useCallback(
    (taskId: string) => {
      const body = (draftsRef.current[taskId] ?? '').trim();
      if (!body) return;
      setDrafts((d) => ({ ...d, [taskId]: '' }));
      void act('Could not post the comment', () => api.comment(taskId, body));
    },
    [act],
  );

  const onRowApprove = useCallback(
    (taskId: string) => void act('Could not approve', () => api.approve(taskId)),
    [act],
  );
  const onRowSignOff = useCallback(
    (taskId: string) => void act('Could not sign off', () => api.signOff(taskId)),
    [act],
  );
  const onRowRetry = useCallback(
    (taskId: string) => void act('Could not resend', () => api.redispatch(taskId)),
    [act],
  );

  const people = useMemo(() => {
    // In "All projects" the same person can be a member row twice — once per
    // project they're on — so this dedupes by profile id rather than
    // producing "From Jane" in the list twice.
    const byId = new Map<string, (typeof data.profiles)[string]>();
    for (const member of Object.values(data.members)) {
      const profile = data.profiles[member.user_id];
      if (profile) byId.set(profile.id, profile);
    }
    return [...byId.values()];
  }, [data.members, data.profiles]);

  const projectName = useCallback(
    (projectId: string) => projects.find((p) => p.id === projectId)?.name ?? null,
    [projects],
  );

  const statusOptions: Option[] = [
    { value: 'all', label: 'Any state' },
    { value: 'review', label: 'Ready to review' },
    { value: 'waiting', label: 'Waiting on a person' },
    { value: 'working', label: 'A routine is on it' },
    { value: 'approval', label: 'Approved or signed off' },
  ];

  return (
    <BoardLayout
      title={searching ? 'Search' : heading.title}
      blurb={
        searching
          ? queue === 'trash'
            ? 'Matches in the trash.'
            : 'Matches across every queue — merged, deployed and snoozed included.'
          : blurb
      }
      above={
        /* Only on Tasks. The other queues answer a narrower question — what
           needs me, what is merging — and an un-imported pull request is not
           an answer to any of them. */
        queue === 'tasks' && project && !searching ? (
          <OrphanPrs projectId={project.id} isAdmin={isAdminOfProject(project.id)} />
        ) : null
      }
      toolbar={
        <>
          <ListSearch
            inputRef={searchRef}
            value={filters.query}
            onChange={(query) => setFilters((f) => ({ ...f, query }))}
            placeholder="Search, #58, pr:412"
            label="Search tasks by text, task number or pull request number"
            title="Searches every task's text. #58 finds task or pull request 58; task:58 and pr:58 pick one. Press / to jump here."
          />
          <Select
            size="sm"
            value={filters.status}
            onChange={(status) => setFilters((f) => ({ ...f, status: status as Filters['status'] }))}
            options={statusOptions}
            aria-label="Filter by state"
          />
          <Select
            size="sm"
            value={filters.source}
            onChange={(source) => setFilters((f) => ({ ...f, source: source as Filters['source'] }))}
            options={[
              { value: 'all', label: 'Any source' },
              { value: 'human', label: 'Raised by a person' },
              { value: 'agent', label: 'Raised by a routine' },
              { value: 'import', label: 'Adopted from a pull request' },
              { value: 'background', label: 'Fire and forget' },
            ]}
            aria-label="Filter by source"
          />
          <Select
            size="sm"
            value={filters.kind}
            onChange={(kind) => setFilters((f) => ({ ...f, kind: kind as Filters['kind'] }))}
            options={[{ value: 'all', label: 'Any kind' }, ...kindOptions('No kind')]}
            aria-label="Filter by kind"
          />
          <Select
            size="sm"
            value={filters.origin}
            onChange={(origin) => setFilters((f) => ({ ...f, origin: origin as Filters['origin'] }))}
            options={[
              { value: 'all', label: 'Any origin' },
              ...TASK_ORIGINS.map((origin) => ({ value: origin, label: ORIGIN_LABELS[origin] })),
            ]}
            aria-label="Filter by origin"
          />
          <Select
            size="sm"
            value={filters.category}
            onChange={(category) => setFilters((f) => ({ ...f, category }))}
            options={[
              { value: 'all', label: 'All categories' },
              ...Object.values(data.categories)
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((c) => ({ value: c.id, label: c.label })),
            ]}
            aria-label="Filter by category"
          />
          {Object.keys(data.repos).length > 1 ? (
            <Select
              size="sm"
              value={filters.repo}
              onChange={(repo) => setFilters((f) => ({ ...f, repo }))}
              options={[
                { value: 'all', label: 'All repositories' },
                ...Object.values(data.repos).map((r) => ({
                  value: r.id,
                  label: `${r.owner}/${r.name}`,
                })),
              ]}
              aria-label="Filter by repository"
            />
          ) : null}
          <Select
            size="sm"
            value={filters.from}
            onChange={(from) => setFilters((f) => ({ ...f, from }))}
            options={[
              { value: 'all', label: 'From anyone' },
              ...people.map((p) => ({ value: p.id, label: `From ${p.name}` })),
            ]}
            aria-label="Filter by who raised it"
          />
          {/* The approval queue has its own order — risk, then age — and
              offering another would only hide the riskiest at the top. */}
          {queue === 'approval' && !searching ? null : (
            <Select
              size="sm"
              className="ml-auto"
              value={sort}
              onChange={(value) => setSort(value as SortOrder)}
              options={SORT_OPTIONS}
              aria-label="Sort tasks"
            />
          )}
        </>
      }
      roomForFab={Boolean(project || allProjects)}
      footer={project || allProjects ? <Composer onCreated={(id) => select(id)} /> : null}
      detail={
        selected ? (
          <TaskDetail task={selected} onClose={() => select(null)} onDeleted={() => select(null)} />
        ) : null
      }
      nothingSelected="Choose a task on the left — arrow keys work too."
    >
      {!data.loaded ? (
        <div className="flex flex-col gap-2 px-3 sm:px-0">
          {[0, 1, 2, 3, 4].map((n) => (
            <Shimmer key={n} className="h-[86px] rounded-2xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="px-3 sm:px-0">
          {searching ? (
            <Empty
              title="No matching tasks"
              hint="Try fewer words, or #number for a task or pull request. The filters still apply."
            />
          ) : (
            <Empty title={heading.empty[0]} hint={heading.empty[1]} />
          )}
        </div>
      ) : queue === 'approval' && !searching ? (
        <div className={LIST_CARD}>
          {rows.map((task) => (
            <ApprovalRow
              key={task.id}
              task={task}
              now={now}
              isAdmin={isAdminOfTask(task)}
              selected={task.id === selectedId}
              ciRuns={data.ciRuns}
              projectLabel={allProjects ? projectName(task.project_id) : null}
              onSelect={select}
              onApprove={onRowApprove}
              onSignOff={onRowSignOff}
              onRetry={onRowRetry}
            />
          ))}
        </div>
      ) : (
        <div className={LIST_CARD}>
          {rows.map((task) => (
            <ConnectedTaskRow
              key={task.id}
              task={task}
              data={data}
              now={now}
              selected={task.id === selectedId}
              isAdmin={isAdminOfTask(task)}
              allProjects={allProjects}
              projectName={projectName}
              draft={drafts[task.id] ?? ''}
              onDraftChange={onDraftChange}
              onRowComment={onRowComment}
              onSelect={select}
              onRowApprove={onRowApprove}
              onRowSignOff={onRowSignOff}
              onRowRetry={onRowRetry}
            />
          ))}
        </div>
      )}
    </BoardLayout>
  );
}

/**
 * Builds one row's `ctx` and hands it to `TaskRow`, which is itself
 * `memo`-wrapped.
 *
 * That memo only ever helped if the `ctx` object it compares is actually the
 * same reference across renders where nothing about *this* row changed —
 * and building it inline in a `.map()`, as this used to, handed `TaskRow` a
 * fresh object every time regardless. The practical effect: the whole list
 * re-rendered every clock tick, and again on every single keystroke in any
 * row's own draft box (since the draft lives in the list's state, not the
 * row's — see `TasksScreen`). This component exists to give that `useMemo`
 * a real chance: its dependencies are either primitives or the specific
 * slice of `data` they came from, both of which the reducer only replaces
 * when that particular table actually changes (see `store.ts`), so an
 * unrelated update — another task, another keystroke — leaves them, and
 * therefore `ctx`, and therefore this row, untouched.
 */
const ConnectedTaskRow = memo(function ConnectedTaskRow({
  task,
  data,
  now,
  selected,
  isAdmin,
  allProjects,
  projectName,
  draft,
  onDraftChange,
  onRowComment,
  onSelect,
  onRowApprove,
  onRowSignOff,
  onRowRetry,
}: {
  task: Task;
  data: DataState;
  now: number;
  selected: boolean;
  isAdmin: boolean;
  allProjects: boolean;
  projectName: (projectId: string) => string | null;
  draft: string;
  onDraftChange: (taskId: string, value: string) => void;
  onRowComment: (taskId: string) => void;
  onSelect: (id: string | null) => void;
  onRowApprove: (taskId: string) => void;
  onRowSignOff: (taskId: string) => void;
  onRowRetry: (taskId: string) => void;
}) {
  const originLabel = originText(task, data);
  const agentName = task.agent_id ? (data.agents[task.agent_id]?.name ?? null) : null;
  const projectLabel = allProjects ? projectName(task.project_id) : null;
  const categoryLabel = task.category_id ? (data.categories[task.category_id]?.label ?? null) : null;
  const repoLabel =
    Object.keys(data.repos).length > 1 && task.repo_id ? (data.repos[task.repo_id]?.name ?? null) : null;
  const commentCount = data.commentCounts[task.id] ?? 0;

  const taskId = task.id;
  const handleDraft = useCallback((value: string) => onDraftChange(taskId, value), [taskId, onDraftChange]);
  const handleComment = useCallback(() => onRowComment(taskId), [taskId, onRowComment]);
  const handleSelect = useCallback(
    () => onSelect(selected ? null : taskId),
    [taskId, selected, onSelect],
  );
  const handleApprove = useCallback(() => onRowApprove(taskId), [taskId, onRowApprove]);
  const handleSignOff = useCallback(() => onRowSignOff(taskId), [taskId, onRowSignOff]);
  const handleRetry = useCallback(() => onRowRetry(taskId), [taskId, onRowRetry]);

  const ctx = useMemo<RowContext>(
    () => ({
      now,
      isAdmin,
      selected,
      originLabel,
      agentName,
      projectLabel,
      categoryLabel,
      repoLabel,
      commentCount,
      showThumbnails: true,
      ciRuns: data.ciRuns,
      previewWorkflow: data.settings[task.project_id]?.preview_workflow ?? null,
      draft,
      onDraft: handleDraft,
      onComment: handleComment,
      onSelect: handleSelect,
      onApprove: handleApprove,
      onSignOff: handleSignOff,
      onRetry: handleRetry,
    }),
    [
      now,
      isAdmin,
      selected,
      originLabel,
      agentName,
      projectLabel,
      categoryLabel,
      repoLabel,
      commentCount,
      data.ciRuns,
      data.settings,
      task.project_id,
      draft,
      handleDraft,
      handleComment,
      handleSelect,
      handleApprove,
      handleSignOff,
      handleRetry,
    ],
  );

  return <TaskRow task={task} ctx={ctx} />;
});
